import React from 'react';
import styled from 'styled-components/macro';

/**
 * Prop descriptions
 * value: state variable that contains the input value
 * onChange: state setter function that will update the state variable
 * label: label that will appear on top of the input
 * error: state boolean that tells whether the input is valid/invalid
 * errorText: a red error message that will display underneath the input if the user tries to submit an invalid value
 */
interface Props {
	className?: any;
	value: any;
	onChange: (value: string) => void;
	label?: string;
	type?: any;
	disabled?: boolean;
	error?: boolean;
	errorText?: string;
	fontSize?: string;
	placeholder?: string;
	id?: string;
	required?: boolean;
	tabIndex?: number;
	height?: string;
	marginBottom?: string;
	onBlur?: any;
	inputAlign?: string;
}

const TextInput = ({ className, value, onChange, label, type, disabled, error, errorText, fontSize, placeholder, id, required, tabIndex, height, marginBottom, onBlur, inputAlign }: Props) => {
	return (
		<StyledContainer data-disabled={disabled} className={className} marginBottom={marginBottom} error={error}>
			{label && (
				<StyledLabel htmlFor={id} id="inputLabel" className={className}>
					{label}
				</StyledLabel>
			)}
			<StyledInput
				type={type}
				id={id}
				value={value}
				onChange={(e) => {
					onChange(e.target.value);
				}}
				onBlur={onBlur}
				disabled={disabled}
				data-error={error ? 'true' : undefined}
				fontSize={fontSize}
				inputAlign={inputAlign}
				placeholder={placeholder}
				required={required}
				className={className}
				tabIndex={tabIndex}
				height={height}
			></StyledInput>
			{error && <ErrorLabel id="textInputErrorLabel">{errorText}</ErrorLabel>}
		</StyledContainer>
	);
};

interface FontProps {
	fontSize?: string;
	height?: string;
	inputAlign?: string;
}
interface ContainerProps {
	error?: boolean;
	marginBottom?: string;
}
const StyledContainer = styled.div<ContainerProps>`
	position: relative;
	display: flex;
	flex-direction: column;
	width: 100%;
	margin-bottom: ${(props) => (props.marginBottom ? props.marginBottom : '0px')};
	&[data-disabled] > #inputLabel {
		opacity: 0.2;
	}
`;

const StyledInput = styled.input<FontProps>`
	height: ${(props) => (props.height ? props.height : '40px')};
	background: #ffffff;
	border: 1px solid #919191;
	box-sizing: border-box;
	border-radius: 4px;
	font-family: museo-sans;
	font-size: ${(props) => (props.fontSize ? props.fontSize : '16px')};
	text-align: ${(props) => (props.inputAlign ? props.inputAlign : 'left')};
	line-height: 19px;
	color: #333333;
	padding: 0px 16px;
	&::placeholder {
		font-style: italic;
	}
	&:focus {
		outline: 2px auto #116eee;
	}
	&:disabled {
		opacity: 0.2;
		border: 1px solid #333333;
	}
	&[data-error] {
		border: 2px solid #e81111;
	}
`;

const StyledLabel = styled.label`
	display: block;
	font-family: museo-sans;
	font-style: normal;
	font-weight: normal;
	font-size: 13px;
	line-height: 15px;
	color: #333333;
	padding-bottom: 8px;
`;

const ErrorLabel = styled(StyledLabel)`
	margin-top: 8px;
	color: #e81111;
	padding-bottom: 0;
`;

export default TextInput;
